import { useLayoutEffect, useRef, useState } from 'react';
import { Chip } from '../Chip/Chip';
import type { SelectOption } from './Select';

const CHIP_GAP_PX = 4;

function wireOptionValue(value: string | number): string {
  return String(value);
}

function getOptionChipLabel<T extends string | number>(
  option: SelectOption<T> | undefined,
  value: T
): string {
  if (option && typeof option.label === 'string') {
    return option.label;
  }
  return String(value);
}

function SelectChip<T extends string | number>({
  selectedValue,
  options
}: {
  selectedValue: T;
  options: readonly SelectOption<T>[];
}) {
  const option = options.find(item => item.value === selectedValue);
  return (
    <Chip
      label={getOptionChipLabel(option, selectedValue)}
      size="small"
      variant="neutral"
      chipStyle="filled"
    />
  );
}

/** Renders compact chips in a single row; shows `+N` when not all chips fit. */
export function SelectMultipleChips<T extends string | number>({
  values,
  options
}: {
  values: T[];
  options: readonly SelectOption<T>[];
}) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(values.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measureRoot = measureRef.current;
    if (!container || !measureRoot) {
      return;
    }

    if (values.length === 0) {
      setVisibleCount(0);
      return;
    }

    const measure = () => {
      const containerWidth = container.clientWidth;
      if (containerWidth <= 0) {
        return;
      }

      const chipNodes = measureRoot.querySelectorAll<HTMLElement>('[data-select-chip]');
      const overflowNode = measureRoot.querySelector<HTMLElement>('[data-select-overflow]');
      if (chipNodes.length === 0) {
        return;
      }

      const overflowWidth = overflowNode?.offsetWidth ?? 0;
      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < chipNodes.length; i++) {
        const chipWidth = chipNodes[i].offsetWidth;
        const gapBefore = count > 0 ? CHIP_GAP_PX : 0;
        const remaining = chipNodes.length - i - 1;
        const reserveOverflow = remaining > 0 ? CHIP_GAP_PX + overflowWidth : 0;

        if (usedWidth + gapBefore + chipWidth + reserveOverflow <= containerWidth) {
          usedWidth += gapBefore + chipWidth;
          count++;
        } else {
          break;
        }
      }

      setVisibleCount(prev => (prev !== count ? count : prev));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [values, options]);

  const hiddenCount = values.length - visibleCount;

  return (
    <>
      <span ref={measureRef} className="SmSelect-multipleChips SmSelect-multipleChipsMeasure" aria-hidden>
        {values.map(selectedValue => (
          <span key={wireOptionValue(selectedValue)} data-select-chip>
            <SelectChip selectedValue={selectedValue} options={options} />
          </span>
        ))}
        <span data-select-overflow className="SmSelect-overflowChip">
          <Chip
            label={`+${values.length}`}
            size="small"
            variant="neutral"
            chipStyle="filled"
          />
        </span>
      </span>
      <span ref={containerRef} className="SmSelect-multipleChips">
        {values.slice(0, visibleCount).map(selectedValue => (
          <SelectChip
            key={wireOptionValue(selectedValue)}
            selectedValue={selectedValue}
            options={options}
          />
        ))}
        {hiddenCount > 0 ? (
          <span className="SmSelect-overflowChip">
            <Chip
              label={`+${hiddenCount}`}
              size="small"
              variant="neutral"
              chipStyle="filled"
            />
          </span>
        ) : null}
      </span>
    </>
  );
}
